import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CompanyProfileCard = ({ settings, save }) => {
  const [profile, setProfile] = useState(settings.company_profile || { name: 'OctoBoots', address: '', sst_reg_no: '' });
  useEffect(() => { setProfile(settings.company_profile || { name: 'OctoBoots', address: '', sst_reg_no: '' }); }, [settings.company_profile]);
  return (
    <Card className="p-6 space-y-4">
      <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Company Profile</h3>
      <p className="text-xs text-slate-500 -mt-2">Appears on Invoice, DO and Partner Statement PDFs.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Company Name</Label><Input data-testid="company-name-input" value={profile.name || ''} onChange={e => setProfile({ ...profile, name: e.target.value })} /></div>
        <div><Label>SST Registration No.</Label><Input data-testid="company-sst-reg" value={profile.sst_reg_no || ''} onChange={e => setProfile({ ...profile, sst_reg_no: e.target.value })} /></div>
        <div className="md:col-span-2"><Label>Address</Label><Input data-testid="company-address" value={profile.address || ''} onChange={e => setProfile({ ...profile, address: e.target.value })} /></div>
      </div>
      <Button data-testid="save-company-profile" onClick={() => save('company_profile', profile)} style={{ backgroundColor: '#1E3A8A' }}>Save Company Profile</Button>
    </Card>
  );
};

export const SettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purge1, setPurge1] = useState('');
  const [purge2, setPurge2] = useState('');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data || {});
    } catch (error) { console.error(error); }
  };

  const saveSetting = async (key, value) => {
    setLoading(true);
    try {
      await api.put(`/settings/${key}`, { key, value });
      toast.success(`Saved ${key}`);
      setSettings({ ...settings, [key]: value });
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handlePurge = async () => {
    if (!purge1 || !purge2) return toast.error('Both confirmations required');
    try {
      const res = await api.post('/admin/purge-demo-data', { confirmation_1: purge1, confirmation_2: purge2 });
      toast.success('Demo data purged');
      setPurgeOpen(false);
      setPurge1(''); setPurge2('');
      console.log(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resetInvoiceCounter = async () => {
    try {
      const res = await api.post('/admin/reset-invoice-counter');
      toast.success(`Counter reset. Next number: ${res.data.next_number}`);
      fetchSettings();
    } catch { toast.error('Failed'); }
  };

  const splitDefaults = settings.partner_split_defaults || { partner_percentage: 65, distributor_percentage: 5, pharmacy_percentage: 30 };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Settings</h1>

      <CompanyProfileCard settings={settings} save={saveSetting} />

      <Card className="p-6 space-y-4">
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>SST (Sales & Service Tax)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>SST Rate (%)</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="sst-rate-input" type="number" step="0.1" value={settings.sst_rate ?? 8} onChange={e => setSettings({ ...settings, sst_rate: parseFloat(e.target.value) })} />
              <Button onClick={() => saveSetting('sst_rate', settings.sst_rate ?? 8)} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
          <div>
            <Label>SST Default</Label>
            <div className="flex gap-2 mt-2 items-center">
              <input data-testid="sst-default-checkbox" type="checkbox" checked={!!settings.sst_enabled_default} onChange={e => setSettings({ ...settings, sst_enabled_default: e.target.checked })} />
              <span className="text-sm">Enable SST on new invoices by default</span>
              <Button size="sm" onClick={() => saveSetting('sst_enabled_default', !!settings.sst_enabled_default)} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Partner Split Defaults (used when creating a new partner)</h3>
        <div className="grid grid-cols-3 gap-4">
          <div><Label>Partner %</Label><Input data-testid="split-partner" type="number" step="0.1" value={splitDefaults.partner_percentage} onChange={e => setSettings({ ...settings, partner_split_defaults: { ...splitDefaults, partner_percentage: parseFloat(e.target.value) } })} /></div>
          <div><Label>Distributor %</Label><Input data-testid="split-distributor" type="number" step="0.1" value={splitDefaults.distributor_percentage} onChange={e => setSettings({ ...settings, partner_split_defaults: { ...splitDefaults, distributor_percentage: parseFloat(e.target.value) } })} /></div>
          <div><Label>Pharmacy %</Label><Input data-testid="split-pharmacy" type="number" step="0.1" value={splitDefaults.pharmacy_percentage} onChange={e => setSettings({ ...settings, partner_split_defaults: { ...splitDefaults, pharmacy_percentage: parseFloat(e.target.value) } })} /></div>
        </div>
        <p className="text-xs text-slate-500">Total: {(splitDefaults.partner_percentage || 0) + (splitDefaults.distributor_percentage || 0) + (splitDefaults.pharmacy_percentage || 0)}% (must equal 100)</p>
        <Button data-testid="save-split-defaults" onClick={() => saveSetting('partner_split_defaults', splitDefaults)} style={{ backgroundColor: '#1E3A8A' }}>Save Defaults</Button>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Business Rules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Default Commission %</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="commission-input" type="number" step="0.1" value={settings.default_commission_percent ?? 30} onChange={e => setSettings({ ...settings, default_commission_percent: parseFloat(e.target.value) })} />
              <Button onClick={() => saveSetting('default_commission_percent', settings.default_commission_percent ?? 30)} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
          <div>
            <Label>Low Stock Threshold</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="low-stock-input" type="number" value={settings.low_stock_threshold ?? 10} onChange={e => setSettings({ ...settings, low_stock_threshold: parseInt(e.target.value) })} />
              <Button onClick={() => saveSetting('low_stock_threshold', settings.low_stock_threshold ?? 10)} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
          <div>
            <Label>Dead-Listing Threshold (months)</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="dead-listing-input" type="number" value={settings.dead_listing_threshold ?? 3} onChange={e => setSettings({ ...settings, dead_listing_threshold: parseInt(e.target.value) })} />
              <Button onClick={() => saveSetting('dead_listing_threshold', settings.dead_listing_threshold ?? 3)} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
          <div>
            <Label>Invoice Payment Terms (days)</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="payment-terms-input" type="number" value={settings.invoice_payment_terms ?? 7} onChange={e => setSettings({ ...settings, invoice_payment_terms: parseInt(e.target.value) })} />
              <Button onClick={() => saveSetting('invoice_payment_terms', settings.invoice_payment_terms ?? 7)} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
          <div>
            <Label>Default Language</Label>
            <div className="flex gap-2 mt-2">
              <select data-testid="default-lang-select" value={settings.default_language ?? 'en'} onChange={e => setSettings({ ...settings, default_language: e.target.value })} className="border rounded px-3 py-2 w-full">
                <option value="en">English</option>
                <option value="zh">中文</option>
              </select>
              <Button onClick={() => saveSetting('default_language', settings.default_language ?? 'en')} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Invoice Numbering</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><Label>Prefix</Label><Input data-testid="inv-prefix-input" value={settings.invoice_prefix || 'INV'} onChange={e => setSettings({ ...settings, invoice_prefix: e.target.value })} /></div>
          <div><Label>Year Format</Label>
            <select className="border rounded px-3 py-2 w-full mt-1" value={settings.invoice_year_format || 'YYYY'} onChange={e => setSettings({ ...settings, invoice_year_format: e.target.value })}>
              <option value="YYYY">YYYY</option><option value="YY">YY</option><option value="none">None</option>
            </select>
          </div>
          <div><Label>Digits</Label><Input type="number" value={settings.invoice_digits ?? 4} onChange={e => setSettings({ ...settings, invoice_digits: parseInt(e.target.value) })} /></div>
          <div><Label>Next Number</Label><Input type="number" value={settings.invoice_next_number ?? 1} onChange={e => setSettings({ ...settings, invoice_next_number: parseInt(e.target.value) })} /></div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => {
            saveSetting('invoice_prefix', settings.invoice_prefix || 'INV');
            saveSetting('invoice_year_format', settings.invoice_year_format || 'YYYY');
            saveSetting('invoice_digits', settings.invoice_digits ?? 4);
            saveSetting('invoice_next_number', settings.invoice_next_number ?? 1);
          }} style={{ backgroundColor: '#1E3A8A' }}>Save Numbering</Button>
          <Button variant="outline" onClick={resetInvoiceCounter} data-testid="reset-invoice-counter">Recompute Next Number</Button>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Google Maps</h3>
        <div>
          <Label>Google Maps API Key (public /where-to-buy page)</Label>
          <div className="flex gap-2 mt-2">
            <Input data-testid="google-maps-key-input" type="password" value={settings.google_maps_api_key || ''} onChange={e => setSettings({ ...settings, google_maps_api_key: e.target.value })} placeholder="AIzaSy..." />
            <Button data-testid="save-maps-key" onClick={() => saveSetting('google_maps_api_key', settings.google_maps_api_key || '')} disabled={loading} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
          </div>
        </div>
        <div>
          <Label>Resend API Key (invitation + password reset emails)</Label>
          <div className="flex gap-2 mt-2">
            <Input data-testid="resend-key-input" type="password" value={settings.resend_api_key || ''} onChange={e => setSettings({ ...settings, resend_api_key: e.target.value })} placeholder="re_..." />
            <Button data-testid="save-resend-key" onClick={() => saveSetting('resend_api_key', settings.resend_api_key || '')} style={{ backgroundColor: '#1E3A8A' }}>Save</Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">If empty, emails are logged to server console instead of sent.</p>
        </div>
      </Card>

      <Card className="p-6 space-y-4 border-2 border-red-300">
        <h3 className="font-bold text-lg text-red-700" style={{ fontFamily: 'Manrope' }}>Danger Zone</h3>
        <p className="text-sm text-slate-600">Purge all demo/operational data (pharmacies, partners, products, sales, invoices, etc.). Admin account, settings and invoice numbering will be preserved.</p>
        <Button data-testid="purge-demo-btn" variant="outline" className="border-red-500 text-red-700 hover:bg-red-50" onClick={() => setPurgeOpen(true)}>
          <Trash size={16} className="mr-2" /> Purge Demo Data
        </Button>
      </Card>

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-700">Purge Demo Data — Double Confirmation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">This action is <strong>irreversible</strong>. All operational data will be deleted.</p>
            <div>
              <Label>1. Type <code className="bg-slate-100 px-1 rounded">PURGE</code> exactly:</Label>
              <Input data-testid="purge-confirm-1" value={purge1} onChange={e => setPurge1(e.target.value)} placeholder="PURGE" />
            </div>
            <div>
              <Label>2. Type your admin name to confirm:</Label>
              <Input data-testid="purge-confirm-2" value={purge2} onChange={e => setPurge2(e.target.value)} placeholder="Your full name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)}>Cancel</Button>
            <Button data-testid="purge-confirm-btn" onClick={handlePurge} className="bg-red-600 hover:bg-red-700 text-white">Purge Now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
